<!-- Copyright by the Spark Development Network; Licensed under the Rock Community License -->
<template>
    <fieldset>

        <ValueDetailList :modelValue="topValues" />

        <div class="row">
            <div class="col-md-6">
                <ValueDetailList :modelValue="leftSideValues" />
            </div>

            <div class="col-md-6">
                <ValueDetailList :modelValue="rightSideValues" />
            </div>
        </div>

        <AttributeValuesContainer :modelValue="attributeValues" :attributes="attributes" :numberOfColumns="2" />
    </fieldset>
</template>

<script setup lang="ts">
    import { computed, PropType, ref } from "vue";
    import AttributeValuesContainer from "@Obsidian/Controls/attributeValuesContainer";
    import ValueDetailList from "@Obsidian/Controls/valueDetailList";
    import { ValueDetailListItemBuilder } from "@Obsidian/Core/Controls/valueDetailListItemBuilder";
    import { ValueDetailListItem } from "@Obsidian/Types/Controls/valueDetailListItem";
    import { SystemPhoneNumberBag } from "@Obsidian/ViewModels/Blocks/Communication/SystemPhoneNumberDetail/systemPhoneNumberBag";
    import { SystemPhoneNumberDetailOptionsBag } from "@Obsidian/ViewModels/Blocks/Communication/SystemPhoneNumberDetail/systemPhoneNumberDetailOptionsBag";

    const props = defineProps({
        modelValue: {
            type: Object as PropType<SystemPhoneNumberBag | null>,
            required: false
        },

        options: {
            type: Object as PropType<SystemPhoneNumberDetailOptionsBag>,
            required: true
        }
    });

    // #region Values

    const attributes = ref(props.modelValue?.attributes ?? {});
    const attributeValues = ref(props.modelValue?.attributeValues ?? {});

    // #endregion

    // #region Computed Values

    /** The values to display full-width at the top of the block. */
    const topValues = computed((): ValueDetailListItem[] => {
        const valueBuilder = new ValueDetailListItemBuilder();

        if (!props.modelValue) {
            return valueBuilder.build();
        }

        if (props.modelValue.description) {
            valueBuilder.addTextValue("Description", props.modelValue.description);
        }

        return valueBuilder.build();
    });

    /** The values to display at half-width on the left side of the block. */
    const leftSideValues = computed((): ValueDetailListItem[] => {
        const valueBuilder = new ValueDetailListItemBuilder();

        if (!props.modelValue) {
            return valueBuilder.build();
        }

        valueBuilder.addTextValue("Number", props.modelValue.number ?? "");

        if (props.modelValue.isSmsEnabled) {
            valueBuilder.addHtmlValue("SMS Enabled", `<i class="fa fa-check"></i>`);
        }

        if (props.modelValue.smsReceivedWorkflowType?.text) {
            valueBuilder.addTextValue("SMS Received Workflow", props.modelValue.smsReceivedWorkflowType.text);
        }

        if (props.modelValue.mobileApplicationSite?.text) {
            valueBuilder.addTextValue("Mobile Application", props.modelValue.mobileApplicationSite.text);
        }

        return valueBuilder.build();
    });

    /** The values to display at half-width on the left side of the block. */
    const rightSideValues = computed((): ValueDetailListItem[] => {
        const valueBuilder = new ValueDetailListItemBuilder();

        if (!props.modelValue) {
            return valueBuilder.build();
        }

        if (props.modelValue.assignedToPersonAlias?.text) {
            valueBuilder.addTextValue("Assigned To", props.modelValue.assignedToPersonAlias.text);
        }

        if (props.modelValue.isSmsForwardingEnabled) {
            valueBuilder.addHtmlValue("SMS Forwarding", `<i class="fa fa-check"></i>`);
        }

        if (props.modelValue.smsNotificationGroup?.text) {
            valueBuilder.addTextValue("SMS Notification Group", props.modelValue?.smsNotificationGroup.text);
        }

        return valueBuilder.build();
    });

    // #endregion

    // #region Functions

    // #endregion

    // #region Event Handlers

    // #endregion
</script>
